/**
 * Wave 14 Agent TRIAL - useTrialStatus hook.
 *
 * Powers the global TrialCountdownBanner + the Day-7 NPS modal + the
 * Day-14 lock screen. Polls /wave14/trial/status every 5 minutes so the
 * banner stays current after milestone unlocks (the +7 days extension
 * shows up as ``trial_ends_at`` moving forward).
 */
import { useEffect, useState, useCallback } from 'react'
import { wave14Trial, TrialStatus } from '@/api/wave14Trial'

const POLL_MS = 5 * 60 * 1000

interface UseTrialStatusResult {
  status: TrialStatus | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  daysElapsed: number
}

export function useTrialStatus(): UseTrialStatusResult {
  const [status, setStatus] = useState<TrialStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await wave14Trial.status()
      setStatus(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load trial status')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const id = window.setInterval(refresh, POLL_MS)
    return () => window.clearInterval(id)
  }, [refresh])

  const daysElapsed = (() => {
    if (!status || !status.trial_ends_at) return 0
    const days = 14 - status.days_remaining
    return Math.max(0, days)
  })()

  return { status, loading, error, refresh, daysElapsed }
}
